import {
	Observable, finalize, of, tap,
} from 'rxjs';

import { AuthService, Container, Service } from '../../../../symphony';

import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import {
	REPLIES_COUNT_BY_PAGE,
	getSelectBestAnswerMessage,
	SlDiscussContext,
	getUnselectBestAnswerMessage,
} from '../../global-constants';
import { SlActions } from '../ActionsService/sl-actions';
import { ActionType, RequestType } from '../../global-interface';
import { getBackendErrorMessage } from '../../utils/helpers';
import { getInitialServerSideData } from '../../../../shared/public/SlUtils/getInitialServerSideData';
import { IFlashMessageTypes } from '../../../../shared/public/SlFlashMessage/SlFlashMessage';
import {
	IDiscussToggleAcceptedAnswerResponseData, IDiscussAddReplyResponseData, IDiscussRepliesResponseData, IDiscussPostData,
} from '../../../../api/public/discuss-api';

@Service()
export class SlRepliesService {
	private api = Container.take(SlDiscussContext, SlApiService);

	private dataService = Container.take(SlDiscussContext, SlDataService);

	private authService = Container.take('global', AuthService);

	private actions$ = Container.take(SlDiscussContext, SlActions).actions$;

	private showNotificationAfterToggleAcceptedAnswer = (
		res: IDiscussToggleAcceptedAnswerResponseData,
		isAccepted: boolean,
	) => {
		if (res.error) {
			this.dataService.notificationData$.next({
				message: getBackendErrorMessage(res.error.data),
				type: IFlashMessageTypes.ERROR,
			});

			return;
		}

		this.dataService.notificationData$.next({
			message: isAccepted
				? getSelectBestAnswerMessage()
				: getUnselectBestAnswerMessage(),
			type: IFlashMessageTypes.SUCCESS,
		});
	};

	private addReply = (res: IDiscussAddReplyResponseData) => {
		const replies = this.dataService.replies$.value;

		this.dataService.replies$.next([
			{
				message: res.post.message,
				id: res.post.id,
				vote: 0,
				votes: res.post.votes,
				userID: res.post.userID,
				userName: this.authService.getUser().name,
				avatarUrl: this.authService.getUser().avatarUrl,
			},
			...replies,
		]);
	};

	public triggerReplyFormVisibility = (isOpen: boolean): Observable<null> => {
		this.dataService.isReplyFormOpen$.next(isOpen);
		this.dataService.editingReplyId$.next(null);
		this.dataService.editingReplyText$.next('');
		return of(null);
	};

	public getReplies = (
		id: string,
		currentPageNumber: number,
		sortType: number,
	): Observable<IDiscussRepliesResponseData> => {
		const ssrReplies = getInitialServerSideData('ssrReplies');

		if (ssrReplies) {
			this.dataService.replies$.next(ssrReplies.posts);

			return of(null);
		}

		return this.api
			.getReplies(
				+id,
				(currentPageNumber - 1) * REPLIES_COUNT_BY_PAGE,
				REPLIES_COUNT_BY_PAGE,
				sortType,
			)
			.pipe(
				tap((replies) => {
					this.dataService.replies$.next(replies.posts);
				}),
			);
	};

	public createReply = (
		replyContent: string,
	): Observable<IDiscussAddReplyResponseData> => {
		const questionId = this.dataService.question$.value?.id;

		return this.api.createReply(+questionId, replyContent).pipe(
			tap((res) => {
				if (res.error) {
					this.dataService.notificationData$.next({
						message: getBackendErrorMessage(res.error.data),
						type: IFlashMessageTypes.ERROR,
					});

					return;
				}

				this.addReply(res);

				this.dataService.question$.next({
					...this.dataService.question$.value,
					answers: this.dataService.question$.value.answers + 1,
				});

				this.actions$.next({
					type: ActionType.TriggerReplyFormVisibility,
					requestType: RequestType.Parallel,
					data: {
						isReplyFormOpen: false,
					},
				});

				this.actions$.next({
					type: ActionType.TriggerCreateReplySuccessMessageVisibility,
					requestType: RequestType.Parallel,
					data: {
						isCreateReplySuccessMessageVisible: true,
					},
				});
			}),
		);
	};

	private updateReplyInStore = (res: IDiscussPostData, id: number) => {
		this.dataService.replies$.next(
			this.dataService.replies$.value.map((reply) => {
				if (reply.id === id) {
					return res.post;
				}

				return reply;
			}),
		);
	};

	public editReply = (
		replyContent: string,
		id: number,
	): Observable<IDiscussPostData> => this.api.editReply(id, replyContent).pipe(
		tap((res) => {
			if (res.error) {
				this.dataService.notificationData$.next({
					message: getBackendErrorMessage(res.error.data),
					type: IFlashMessageTypes.ERROR,
				});

				return;
			}

			this.updateReplyInStore(res, id);

			this.actions$.next({
				type: ActionType.TriggerReplyFormVisibility,
				requestType: RequestType.Parallel,
				data: {
					isReplyFormOpen: false,
				},
			});

			this.actions$.next({
				type: ActionType.TriggerEditReplySuccessMessageVisibility,
				requestType: RequestType.Parallel,
				data: {
					isEditReplySuccessMessageVisible: true,
				},
			});
		}),
	);

	public triggerCreateReplySuccessMessageVisibility = (
		isVisible: boolean,
	): Observable<null> => {
		this.dataService.isVisibleCreateReplySuccessMessage$.next(isVisible);

		return of(null);
	};

	public triggerEditReplySuccessMessageVisibility = (
		isVisible: boolean,
	): Observable<null> => {
		this.dataService.isVisibleEditReplySuccessMessage$.next(isVisible);

		return of(null);
	};

	private getIsAcceptedAnswer = (id: number) => this.dataService.replies$.value.find((reply) => reply.id === id)
		.isAccepted;

	public toggleAcceptedAnswer = (
		id: number,
	): Observable<IDiscussToggleAcceptedAnswerResponseData> => {
		const isAcceptedAnswer = this.getIsAcceptedAnswer(id);

		return this.api.toggleAcceptedAnswer(id, !isAcceptedAnswer).pipe(
			tap((res) => {
				if (!res.error) {
					this.dataService.replies$.next(
						this.dataService.replies$.value.map((reply) => ({
							...reply,
							isAccepted:
								reply.id === id ? !reply.isAccepted : false,
						})),
					);
				}

				this.showNotificationAfterToggleAcceptedAnswer(
					res,
					!isAcceptedAnswer,
				);
			}),
		);
	};

	public onReplyEditButtonClick = (
		id: number,
		text: string,
	): Observable<null> => {
		this.dataService.isReplyFormOpen$.next(true);
		this.dataService.editingReplyId$.next(id);
		this.dataService.editingReplyText$.next(text);

		return of(null);
	};

	public deleteReply = (): Observable<null> => {
		const id = this.dataService.deletingReplyId$.value;
		this.dataService.deleteReplyLoading$.next(true);

		this.api
			.deleteReply(id)
			.pipe(
				tap(() => {
					this.dataService.question$.next({
						...this.dataService.question$.value,
						answers: this.dataService.question$.value.answers - 1,
					});
					this.dataService.isReplyFormOpen$.next(false);
					this.dataService.showDeleteReplyModal$.next(false);
					this.dataService.editingReplyId$.next(null);
					this.dataService.editingReplyText$.next('');
					this.dataService.replies$.next(
						this.dataService.replies$.value.filter(
							(reply) => reply.id !== id,
						),
					);

					return of(null);
				}),
				finalize(() => {
					this.dataService.deleteReplyLoading$.next(false);
				}),
			)
			.subscribe();

		return of(null);
	};
}
